import { Check, Copy, Link2, Loader2, ShieldCheck } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { api } from "../../api/client";

interface InviteItem {
	id: number;
	kind: string;
	trialDays: number | null;
	expiresAt: string | Date;
	createdBy: string | null;
	createdAt: string | Date;
	acceptedAt: string | Date | null;
	acceptedEmail: string | null;
	revokedAt: string | Date | null;
	status: "open" | "accepted" | "revoked" | "expired";
}

const STATUS_STYLE: Record<InviteItem["status"], string> = {
	open: "bg-sky-50 text-sky-700",
	accepted: "bg-emerald-50 text-emerald-700",
	revoked: "bg-slate-100 text-slate-500",
	expired: "bg-amber-50 text-amber-700",
};

const fmtDate = (value: string | Date | null) =>
	value
		? new Date(value).toLocaleString("da-DK", {
				dateStyle: "medium",
				timeStyle: "short",
			})
		: "—";

export const CssInvitePanel = ({
	merchantId,
	affiliatePcss,
}: {
	merchantId: string;
	affiliatePcss: boolean;
}) => {
	const [invites, setInvites] = useState<InviteItem[]>([]);
	const [loading, setLoading] = useState(true);
	const [creating, setCreating] = useState(false);
	const [trialDays, setTrialDays] = useState("");
	const [validDays, setValidDays] = useState("30");
	const [newUrl, setNewUrl] = useState<string | null>(null);
	const [copied, setCopied] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const load = useCallback(async () => {
		setLoading(true);
		const { data } = await api.merchants({ id: merchantId }).invites.get();
		setInvites(Array.isArray(data) ? (data as unknown as InviteItem[]) : []);
		setLoading(false);
	}, [merchantId]);

	useEffect(() => {
		load();
	}, [load]);

	const create = async () => {
		setCreating(true);
		setError(null);
		setNewUrl(null);
		setCopied(false);
		const trial = Number(trialDays);
		const valid = Number(validDays);
		const { data, error: err } = await api
			.merchants({ id: merchantId })
			.invites.post({
				kind: "AFFILIATE_PCSS",
				...(trial > 0 ? { trialDays: trial } : {}),
				...(valid > 0 ? { validDays: valid } : {}),
			});
		setCreating(false);
		if (err || !data || !("url" in data)) {
			const message =
				(err?.value as { error?: string } | undefined)?.error ??
				(data as { error?: string } | null)?.error ??
				"Could not create the invite";
			setError(message);
			return;
		}
		setNewUrl(data.url as string);
		load();
	};

	const revoke = async (inviteId: number) => {
		await api
			.merchants({ id: merchantId })
			.invites({ inviteId: String(inviteId) })
			.revoke.post();
		load();
	};

	const copy = async () => {
		if (!newUrl) return;
		await navigator.clipboard.writeText(newUrl);
		setCopied(true);
	};

	return (
		<div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
			<div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
				<ShieldCheck className="w-4 h-4 text-slate-400" />
				<h2 className="text-sm font-semibold text-slate-700">
					Premium CSS invitation
				</h2>
				{affiliatePcss && (
					<span className="ml-2 inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700">
						Affiliate + Premium CSS
					</span>
				)}
			</div>
			<div className="p-6 space-y-5">
				<p className="text-sm text-slate-500">
					Creates a single-use link that lets this affiliate create a webapp
					account and buy Premium CSS while staying an affiliate. The link is
					shown once — copy it before leaving the page.
				</p>

				<div className="flex flex-wrap items-end gap-3">
					<label className="text-xs text-slate-500">
						Free trial (days)
						<input
							type="number"
							min={0}
							max={365}
							value={trialDays}
							onChange={(e) => setTrialDays(e.target.value)}
							placeholder="0"
							className="mt-1 block w-28 rounded-lg border border-slate-200 px-3 py-2 text-sm"
						/>
					</label>
					<label className="text-xs text-slate-500">
						Link valid (days)
						<input
							type="number"
							min={1}
							max={90}
							value={validDays}
							onChange={(e) => setValidDays(e.target.value)}
							className="mt-1 block w-28 rounded-lg border border-slate-200 px-3 py-2 text-sm"
						/>
					</label>
					<button
						type="button"
						onClick={create}
						disabled={creating}
						className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
					>
						{creating ? (
							<Loader2 className="w-4 h-4 animate-spin" />
						) : (
							<Link2 className="w-4 h-4" />
						)}
						Create invite link
					</button>
				</div>

				{error && <p className="text-sm text-red-600">{error}</p>}

				{newUrl && (
					<div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
						<code className="flex-1 truncate text-xs text-emerald-800">
							{newUrl}
						</code>
						<button
							type="button"
							onClick={copy}
							className="inline-flex items-center gap-1 rounded-md bg-white px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200"
						>
							{copied ? (
								<Check className="w-3.5 h-3.5" />
							) : (
								<Copy className="w-3.5 h-3.5" />
							)}
							{copied ? "Copied" : "Copy"}
						</button>
					</div>
				)}

				{loading ? (
					<Loader2 className="w-4 h-4 animate-spin text-slate-400" />
				) : invites.length > 0 ? (
					<table className="w-full text-sm">
						<thead>
							<tr className="text-left text-xs text-slate-400">
								<th className="py-2 pr-3 font-medium">Status</th>
								<th className="py-2 pr-3 font-medium">Created</th>
								<th className="py-2 pr-3 font-medium">Expires</th>
								<th className="py-2 pr-3 font-medium">Trial</th>
								<th className="py-2 pr-3 font-medium">Accepted by</th>
								<th className="py-2" />
							</tr>
						</thead>
						<tbody>
							{invites.map((inv) => (
								<tr key={inv.id} className="border-t border-slate-100">
									<td className="py-2 pr-3">
										<span
											className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_STYLE[inv.status]}`}
										>
											{inv.status}
										</span>
									</td>
									<td className="py-2 pr-3 text-slate-600">
										{fmtDate(inv.createdAt)}
										{inv.createdBy ? ` · ${inv.createdBy}` : ""}
									</td>
									<td className="py-2 pr-3 text-slate-600">
										{fmtDate(inv.expiresAt)}
									</td>
									<td className="py-2 pr-3 text-slate-600">
										{inv.trialDays ? `${inv.trialDays} d` : "—"}
									</td>
									<td className="py-2 pr-3 text-slate-600">
										{inv.acceptedEmail ?? "—"}
									</td>
									<td className="py-2 text-right">
										{inv.status === "open" && (
											<button
												type="button"
												onClick={() => revoke(inv.id)}
												className="text-xs font-semibold text-red-600 hover:underline"
											>
												Revoke
											</button>
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				) : (
					<p className="text-sm text-slate-400">No invites yet.</p>
				)}
			</div>
		</div>
	);
};
